import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    KAL_DIALOG_DATA,
    KalDialogConfig,
    KalDialogRef,
    KalDialogService,
    KalSnackbarService
} from '@kalidea/kaligraphi';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    MutationGetListePortailDocumentCategorieGeneriqueArgs,
    MutationSaveListeCategoriesGedArgs,
    PortailDocumentCategorieDto,
    StatusDtoEnum
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { filter, tap } from 'rxjs/operators';
import { UntypedFormControl, Validators } from '@angular/forms';
import { FormValidator } from 'src/app/validators/form-validator';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { KalTableComponent } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import cloneDeep from 'lodash-es/cloneDeep';

export interface DocumentCategoryEditDialogData {
    accepted?: boolean;
    categoryList: PortailDocumentCategorieDto[];
}

type WebAdministrationDocumentCategoryEditDialog = PortailDocumentCategorieDto & {
    CodeControl: UntypedFormControl, LibelleControl: UntypedFormControl
};

const MAXIMUM_CHARACTER_CODE = 6;
const MAXIMUM_CHARACTER_LIBELLE = 255;

@Component({
    selector: 'app-document-category-edit-dialog',
    templateUrl: './document-category-edit-dialog.component.html',
    styleUrls: ['./document-category-edit-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DocumentCategoryEditDialogComponent extends BaseComponent implements OnInit {

    @ViewChild(KalTableComponent) tableComponent: KalTableComponent<WebAdministrationDocumentCategoryEditDialog>;

    readonly columns: KalTableHeader<WebAdministrationDocumentCategoryEditDialog>[] = [{
        code: 'CodeControl',
        label: 'Code',
        sortable: true
    }, {code: 'LibelleControl', label: 'Libellé', sortable: true},];

    categoryList: WebAdministrationDocumentCategoryEditDialog[] = [];

    constructor(protected readonly cdr: ChangeDetectorRef,
                private dialogRef: KalDialogRef<DocumentCategoryEditDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public readonly data: DocumentCategoryEditDialogData,
                private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService,
                private readonly dialogService: KalDialogService) {
        super(cdr);
    }

    get shouldDisableSaveButton(): boolean {
        return this.categoryList.some(e => e.CodeControl.invalid || e.LibelleControl.invalid) || this.loading;
    }

    virtualRowClassFunction = (category: WebAdministrationDocumentCategoryEditDialog) => category.Status === StatusDtoEnum.Deleted ? 'closedCategory' : '';

    stopPropagation($event: MouseEvent): void {
        $event.stopPropagation();
    }

    save(): void {
        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'SaveListeCategoriesGED', MutationSaveListeCategoriesGedArgs>({
            mutation: gql`
                mutation SaveListeCategoriesGED(
                    $contexte: ContextInfosInput
                    $ListeDocumentCategorie: ArrayOfPortailDocumentCategorieDTOInput
                ) {
                    SaveListeCategoriesGED(
                        contexte: $contexte
                        ListeDocumentCategorie: $ListeDocumentCategorie
                    ) {
                        PortailDocumentCategorieDTO {
                            Code
                            Id
                            Libelle
                        }
                    }
                }
            `,
            variables: {
                ListeDocumentCategorie: {
                    PortailDocumentCategorieDTO: this.normalize()
                }
            }
        }).pipe(tap(() => {
            this.snackbarService.open({title: 'Les catégories ont bien été enregistrées'});
            this.dialogRef.close({
                accepted: true, categoryList: this.normalize()
            });
        }), wheneverEnd(() => this.loading = false));
    }

    deleteCategory(index: number): void {
        const category = this.categoryList[index];

        const config = new KalDialogConfig<ConfirmDialogData>({
            title: `Suppression de la catégorie ${category.Libelle}`,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                body: 'Etes vous sûr de vouloir supprimer cette catégorie ?',
                submitLabel: 'Supprimer',
                cancelLabel: 'Annuler'
            }
        });

        this.watch$ = this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
            filter(e => !!e?.accepted),
            tap(() => {
                if (category.Id) {
                    category.Status = StatusDtoEnum.Deleted;
                }

                const categoriesList = cloneDeep(this.categoryList);
                categoriesList.splice(index, 1);
                this.categoryList = categoriesList;

                this.cdr.markForCheck();
            })
        )
    }

    addCategory(): void {
        const categoriesList = cloneDeep(this.categoryList);
        categoriesList.unshift(DocumentCategoryEditDialogComponent.createCategory());
        this.categoryList = categoriesList;

        this.cdr.markForCheck();
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private getCategoryList(): void {
        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'GetListePortailDocumentCategorieGenerique', MutationGetListePortailDocumentCategorieGeneriqueArgs>({
            mutation: gql`
                mutation GetListePortailDocumentCategorieGenerique(
                    $contexte: ContextInfosInput
                    $criteres: CriteresPortailDocumentCategorieDTOInput
                ) {
                    GetListePortailDocumentCategorieGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        PortailDocumentCategorieDTO {
                            Code
                            Id
                            Libelle
                        }
                    }
                }
            `
        }).pipe(
            tap(GetListePortailDocumentCategorieGenerique => {
                this.categoryList = GetListePortailDocumentCategorieGenerique?.PortailDocumentCategorieDTO.map(e => DocumentCategoryEditDialogComponent.createCategory(e)) || [];
            }),
            wheneverEnd(() => this.loading = false));
    }

    private static createCategory(category?: PortailDocumentCategorieDto): WebAdministrationDocumentCategoryEditDialog {
        return {
            ...(category || {}),
            Status: category?.Id ? StatusDtoEnum.Updated : StatusDtoEnum.Inserted,
            CodeControl: new UntypedFormControl(category?.Code || null, [FormValidator.isNotNull, Validators.maxLength(MAXIMUM_CHARACTER_CODE)]),
            LibelleControl: new UntypedFormControl(category?.Libelle || null, [FormValidator.isNotNull, Validators.maxLength(MAXIMUM_CHARACTER_LIBELLE)])
        };
    }

    private normalize(): PortailDocumentCategorieDto[] {
        return this.categoryList.map(e => {
            const value: WebAdministrationDocumentCategoryEditDialog = {
                ...e, Code: e.CodeControl.value, Libelle: e.LibelleControl.value
            };

            delete value.CodeControl;
            delete value.LibelleControl;

            return value;
        });
    }

    ngOnInit(): void {
        this.getCategoryList();
    }

}
